import React from 'react';

function formatRel(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Tiny muted suffix — only for alpha/premium so starter rows stay clean. */
function tierHint(tier) {
  const t = tier === 'enterprise' ? 'premium' : (tier === 'free' ? 'starter' : tier);
  if (t !== 'alpha' && t !== 'premium') return null;
  return (
    <span className="text-[10px] text-gray-400 dark:text-gray-500 font-normal shrink-0">
      · {t === 'alpha' ? 'α' : 'pro'}
    </span>
  );
}

function SummaryCard({ label, value, hint, accent = 'blue' }) {
  const accents = {
    blue: 'border-blue-200 dark:border-blue-800',
    amber: 'border-amber-300 dark:border-amber-700',
    green: 'border-green-200 dark:border-green-800',
    red: 'border-red-200 dark:border-red-800',
    purple: 'border-purple-200 dark:border-purple-800',
    gray: 'border-gray-200 dark:border-gray-700',
  };
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border ${accents[accent] || accents.gray} p-4 shadow-sm`}>
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      {hint ? <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">{hint}</p> : null}
    </div>
  );
}

function LeaderboardCard({ title, subtitle, rows, renderMetric, empty = 'No data yet', onSelect }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        {subtitle ? <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p> : null}
      </div>
      {!rows?.length ? (
        <div className="px-4 py-6 text-sm text-gray-400 text-center">{empty}</div>
      ) : (
        <ol className="divide-y divide-gray-50 dark:divide-gray-700/80">
          {rows.map((row, i) => (
            <li key={row.id || row.telegramId || i}>
              <button
                type="button"
                onClick={() => onSelect?.(row)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
              >
                <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${
                  i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-gray-300 text-gray-800' : i === 2 ? 'bg-amber-700 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                }`}>
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.name}</span>
                    {tierHint(row.tier)}
                  </div>
                  <p className="text-[11px] text-gray-400 truncate">{row.telegramId}</p>
                </div>
                <div className="text-right shrink-0">
                  {renderMetric(row)}
                </div>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export function GroupAnalyticsPanel({ analytics, loading, onSelectRow }) {
  if (loading) {
    return <div className="mb-6 text-sm text-gray-400">Loading analytics…</div>;
  }
  if (!analytics?.summary) return null;
  const s = analytics.summary;
  const lb = analytics.leaderboards || {};

  return (
    <div className="mb-8 space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <SummaryCard label="Total groups" value={s.total} accent="blue" />
        <SummaryCard label="Active" value={s.active} hint={`${s.inactive} inactive`} accent="green" />
        <SummaryCard label="Premium" value={s.premiumCount} hint={s.premiumExpiringSoon ? `${s.premiumExpiringSoon} expire in 7d` : 'paid tier'} accent="purple" />
        <SummaryCard label="Alpha" value={s.alphaCount} hint="lifetime free" accent="amber" />
        <SummaryCard label="Starter" value={s.starterCount} accent="gray" />
        <SummaryCard label="Tokens tracked" value={s.totalTokensTracked} accent="blue" />
        <SummaryCard label="Alerts sent" value={s.totalAlertsSent} accent="green" />
        <SummaryCard label="Muted" value={s.muted} hint={`${s.botRemoved || 0} bot removed`} accent="red" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Scanning now" value={s.scanning?.active || 0} hint="last scan under 24h" accent="green" />
        <SummaryCard label="Idle" value={s.scanning?.idle || 0} hint="24h–7d" accent="amber" />
        <SummaryCard label="Inactive scans" value={s.scanning?.inactive || 0} hint="over 7d quiet" accent="red" />
        <SummaryCard label="No tokens" value={s.scanning?.none || 0} accent="gray" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <LeaderboardCard
          title="Most tokens tracked"
          subtitle="Live contracts currently scanning"
          rows={lb.mostTokens}
          onSelect={onSelectRow}
          renderMetric={(r) => (
            <>
              <p className="text-sm font-bold text-gray-900 dark:text-white">{r.tokensTracked}</p>
              <p className="text-[10px] text-gray-400">{r.scanActivity}</p>
            </>
          )}
        />
        <LeaderboardCard
          title="Most alerts sent"
          subtitle="Lifetime + recovery alerts lifetime"
          rows={lb.mostAlerts}
          onSelect={onSelectRow}
          renderMetric={(r) => (
            <>
              <p className="text-sm font-bold text-blue-600">{r.totalAlertsSent}</p>
              <p className="text-[10px] text-gray-400">{r.tokensTracked} tokens</p>
            </>
          )}
        />
        <LeaderboardCard
          title="Highest multiplier"
          subtitle="Best milestone hit in any tracked cook"
          rows={lb.highestMultiplier}
          onSelect={onSelectRow}
          renderMetric={(r) => (
            <>
              <p className="text-sm font-bold text-amber-600">{r.multiplier}x</p>
              <p className="text-[10px] text-gray-400">{r.tokenSymbol}</p>
            </>
          )}
        />
        <LeaderboardCard
          title="Best recovery"
          subtitle="Highest recovery % observed"
          rows={lb.highestRecovery}
          onSelect={onSelectRow}
          renderMetric={(r) => (
            <>
              <p className="text-sm font-bold text-green-600">+{r.recoveryPct}%</p>
              <p className="text-[10px] text-gray-400">{r.tokenSymbol}</p>
            </>
          )}
        />
        <LeaderboardCard
          title="Most recently active"
          subtitle="Last scan / command activity"
          rows={lb.mostActive}
          onSelect={onSelectRow}
          renderMetric={(r) => (
            <>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatRel(r.lastScanAt || r.lastActiveAt)}</p>
              <p className="text-[10px] text-gray-400">{r.tokensTracked} tokens</p>
            </>
          )}
        />
        <LeaderboardCard
          title="Low / quiet groups"
          subtitle="Active groups with idle or no tokens"
          rows={lb.lowActivity}
          onSelect={onSelectRow}
          empty="All active groups look healthy"
          renderMetric={(r) => (
            <>
              <p className="text-sm font-medium text-red-500">{r.scanActivity || 'none'}</p>
              <p className="text-[10px] text-gray-400">{formatRel(r.lastScanAt || r.lastActiveAt)}</p>
            </>
          )}
        />
      </div>

      <LeaderboardCard
        title="Premium & Alpha groups"
        subtitle="Paid / lifetime tiers ranked by tokens tracked"
        rows={lb.premiumGroups}
        onSelect={onSelectRow}
        empty="No Premium or Alpha groups yet"
        renderMetric={(r) => (
          <>
            <p className="text-sm font-bold text-gray-900 dark:text-white">{r.tokensTracked} tokens</p>
            <p className="text-[10px] text-gray-400">
              {r.totalAlertsSent || 0} alerts
              {r.membershipExpiryDate ? ` · exp ${formatRel(r.membershipExpiryDate)}` : ''}
              {r.alertsMuted ? ' · muted' : ''}
            </p>
          </>
        )}
      />
    </div>
  );
}

export function UserAnalyticsPanel({ analytics, loading, onSelectRow }) {
  if (loading) {
    return <div className="mb-6 text-sm text-gray-400">Loading analytics…</div>;
  }
  if (!analytics?.summary) return null;
  const s = analytics.summary;
  const lb = analytics.leaderboards || {};

  return (
    <div className="mb-8 space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <SummaryCard label="Total users" value={s.total} accent="blue" />
        <SummaryCard label="Active" value={s.active} hint={`${s.inactive} inactive`} accent="green" />
        <SummaryCard label="Active 7d" value={s.active7d} accent="green" />
        <SummaryCard label="Active 30d" value={s.active30d} accent="blue" />
        <SummaryCard label="Premium" value={s.premiumCount} hint={s.premiumExpiringSoon ? `${s.premiumExpiringSoon} expire in 7d` : 'paid tier'} accent="purple" />
        <SummaryCard label="Alpha" value={s.alphaCount} hint="lifetime free" accent="amber" />
        <SummaryCard label="Starter" value={s.starterCount} accent="gray" />
        <SummaryCard label="Tokens tracked" value={s.totalTokensTracked} accent="blue" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <LeaderboardCard
          title="Most tokens tracked"
          subtitle="Private chat cooks currently scanning"
          rows={lb.mostTokens}
          onSelect={onSelectRow}
          renderMetric={(r) => (
            <>
              <p className="text-sm font-bold text-gray-900 dark:text-white">{r.tokensTracked}</p>
              <p className="text-[10px] text-gray-400">{formatRel(r.lastActiveAt)}</p>
            </>
          )}
        />
        <LeaderboardCard
          title="Highest multiplier"
          subtitle="Best milestone hit"
          rows={lb.highestMultiplier}
          onSelect={onSelectRow}
          renderMetric={(r) => (
            <>
              <p className="text-sm font-bold text-amber-600">{r.multiplier}x</p>
              <p className="text-[10px] text-gray-400">{r.tokenSymbol}</p>
            </>
          )}
        />
        <LeaderboardCard
          title="Best recovery"
          subtitle="Highest recovery % observed"
          rows={lb.highestRecovery}
          onSelect={onSelectRow}
          renderMetric={(r) => (
            <>
              <p className="text-sm font-bold text-green-600">+{r.recoveryPct}%</p>
              <p className="text-[10px] text-gray-400">{r.tokenSymbol}</p>
            </>
          )}
        />
        <LeaderboardCard
          title="Most recently active"
          subtitle="Last command / activity"
          rows={lb.mostActive}
          onSelect={onSelectRow}
          renderMetric={(r) => (
            <>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatRel(r.lastActiveAt)}</p>
              <p className="text-[10px] text-gray-400">{r.tokensTracked} tokens</p>
            </>
          )}
        />
        <LeaderboardCard
          title="Low activity users"
          subtitle="Active accounts with no tokens tracked"
          rows={lb.lowActivity}
          onSelect={onSelectRow}
          empty="Every active user has tokens"
          renderMetric={(r) => (
            <>
              <p className="text-sm font-medium text-red-500">0 tokens</p>
              <p className="text-[10px] text-gray-400">{formatRel(r.lastActiveAt)}</p>
            </>
          )}
        />
        <LeaderboardCard
          title="Premium & Alpha users"
          subtitle="Paid / lifetime tiers by tokens"
          rows={lb.premiumUsers}
          onSelect={onSelectRow}
          empty="No Premium or Alpha users yet"
          renderMetric={(r) => (
            <>
              <p className="text-sm font-bold text-gray-900 dark:text-white">{r.tokensTracked} tokens</p>
              <p className="text-[10px] text-gray-400">
                {formatRel(r.lastActiveAt)}
                {r.membershipExpiryDate ? ` · exp ${formatRel(r.membershipExpiryDate)}` : ''}
              </p>
            </>
          )}
        />
      </div>
    </div>
  );
}
